import React, { useState } from 'react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Send, MapPin, Phone, Mail, Clock, ArrowRight } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', school: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      toast.success("Message sent! We'll respond within 24 hours.");
      setFormData({ name: '', email: '', phone: '', school: '', message: '' });
    } catch {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const contactInfo = [
    { icon: Mail, label: 'Email', value: 'support@ariskool.in' },
    { icon: Phone, label: 'Phone', value: '+91 98765 43210' },
    { icon: MapPin, label: 'Office', value: 'Bangalore, Karnataka' },
    { icon: Clock, label: 'Hours', value: 'Mon-Sat, 9AM-6PM IST' },
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-white" data-testid="contact-page">
        <section className="relative py-24 px-6 bg-slate-900 overflow-hidden">
          <div className="absolute top-[-100px] left-[30%] w-[500px] h-[500px] rounded-full bg-teal-500/10 blur-[120px] pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10 text-center">
            <p className="text-teal-400 text-sm font-semibold uppercase tracking-wider mb-4">Contact Us</p>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-6" data-testid="contact-title">Get in Touch</h1>
            <p className="text-lg text-slate-400 max-w-xl mx-auto">
              Have questions about Ariskool? Our team is here to help Indian schools modernize their leave management.
            </p>
          </div>
        </section>

        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Contact Information</h2>
              {contactInfo.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div key={idx} className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 hover:border-teal-200 hover:bg-teal-50/30 transition-colors">
                    <div className="h-11 w-11 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-5 w-5 text-teal-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 uppercase tracking-wider">{item.label}</p>
                      <p className="font-medium text-slate-900">{item.value}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="p-8 rounded-2xl border border-slate-200 shadow-sm">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Send us a Message</h2>
                <p className="text-sm text-slate-500 mb-6">Fill in the form and we'll get back to you within 24 hours.</p>
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-600">Your Name *</Label>
                      <Input placeholder="Full name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="rounded-xl" data-testid="contact-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-600">Email *</Label>
                      <Input type="email" placeholder="you@school.ariskool.in" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="rounded-xl" data-testid="contact-email-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-600">Phone</Label>
                      <Input placeholder="+91 98765 43210" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="rounded-xl" data-testid="contact-phone-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-600">School Name</Label>
                      <Input placeholder="e.g. Spring Buds School" value={formData.school} onChange={(e) => setFormData({...formData, school: e.target.value})} className="rounded-xl" data-testid="contact-school-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-600">Message *</Label>
                    <textarea
                      placeholder="Tell us about your school and how we can help..."
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      required
                      rows={4}
                      className="flex w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      data-testid="contact-message-input"
                    />
                  </div>
                  <Button type="submit" className="w-full h-12 bg-teal-600 hover:bg-teal-500 text-white font-semibold rounded-xl" disabled={sending} data-testid="contact-submit-btn">
                    <Send className="h-4 w-4 mr-2" />
                    {sending ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Contact;
